import React, { useMemo } from "react";
import { PhaseStatus } from "./PhaseStatusPill";
import { motion } from "framer-motion";

interface PhaseTimelineProps {
  currentPhase?: PhaseStatus;
  expectedEndDate?: string | Date;
  className?: string;
}

const PhaseTimeline: React.FC<PhaseTimelineProps> = ({
  currentPhase = PhaseStatus.ACTIVE_REPORTING,
  expectedEndDate,
  className = ""
}) => {
  // Determine if the program has passed the expected end date
  const hasCompleted = useMemo(() => {
    if (!expectedEndDate) return false;
    try {
      const date = new Date(expectedEndDate);
      return !isNaN(date.getTime()) && new Date() > date;
    } catch {
      return false;
    }
  }, [expectedEndDate]);

  const phases = [
    {
      key: "PRETESTING" as const,
      label: "Pretesting",
      description: "Review and preparation"
    },
    {
      key: "REPORTING" as const,
      label: "Reporting",
      description: "Active vulnerability reporting"
    },
    {
      key: "COMPLETED" as const,
      label: "Completed",
      description: hasCompleted ? "Program completed" : "Awaiting completion"
    }
  ];

  // Compute current step index: 0=Pretesting, 1=Reporting, 2=Completed
  const currentStepIndex = hasCompleted
    ? 2
    : currentPhase === PhaseStatus.PRETESTING
    ? 0
    : 1;

  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-center justify-between">
        {phases.map((phase, index) => {
          const isCompleted = index < currentStepIndex;
          const isCurrent = index === currentStepIndex;
          const isUpcoming = index > currentStepIndex;

          return (
            <React.Fragment key={phase.key}>
              <div className="flex flex-col items-start">
                {/* Phase Rectangle with gradient */}
                <motion.div
                  className="rounded-md shadow-sm px-6 py-4"
                  style={{
                    background: isUpcoming
                      ? 'linear-gradient(90deg, #E5E7EB 0%, rgba(217,217,217,0) 100%)'
                      : 'linear-gradient(90deg, rgba(88,204,2,0.4) 0%, rgba(217,217,217,0) 100%)'
                  }}
                  initial={{ opacity: 0.85 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                      isCurrent ? 'bg-ctb-blue-600 text-white' : isCompleted ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'
                    }`}>{isCompleted ? (
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : index + 1}</div>
                    <div>
                      <div className={`text-sm font-semibold ${isUpcoming ? 'text-gray-500' : 'text-gray-900'}`}>{phase.label}</div>
                      <div className={`text-xs ${isUpcoming ? 'text-gray-400' : 'text-gray-600'}`}>{phase.description}</div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Connector Line with animated progress */}
              {index < phases.length - 1 && (
                <div className="flex-1 mx-4">
                  <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-green-600"
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(Math.max(currentStepIndex - index, 0), 1) * 100}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default PhaseTimeline;
